// Areas (neighbourhoods) live in public.areas, one row per (city, slug). The
// code catalog in src/data/areas.ts is the seed and the fallback used until
// the table exists; its bundled photos remain the default picture.
import { getAreaBySlug, type AreaData } from "@/data/areas";

import { parseThingsToDo, thingsToDoToJson, type ThingToDo } from "@/lib/thingsToDo";

export type { AreaData };

export interface AreaRow {
  id: string;
  tenant_id: string;
  slug: string;
  name: string;
  short_description: string | null;
  tags: string[] | null;
  best_for: string | null;
  vibe: string | null;
  access: string | null;
  location_bullets: string[] | null;
  known_for: string[] | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_text: string | null;
  lat: number | null;
  lng: number | null;
  published: boolean;
  /** jsonb list of local places (see src/lib/thingsToDo.ts); absent before the collections SQL. */
  things_to_do?: unknown;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** What the site renders: the catalog shape plus where it came from. */
export interface Area extends AreaData {
  id: string | null;
  tenantId: string;
  published: boolean;
  /** False for rows without a map pin (coordinates then hold the city centre). */
  hasPin: boolean;
  /** True when served from the code catalog rather than the database. */
  fromCatalog: boolean;
  thingsToDo: ThingToDo[];
}

/** The editable prose of an area, camel-cased. */
export interface AreaContent {
  shortDescription: string;
  tags: string[];
  bestFor: string;
  vibe: string;
  access: string;
  locationBullets: string[];
  knownFor: string[];
  seoTitle: string;
  seoDescription: string;
  seoText: string;
  thingsToDo: ThingToDo[];
}

export const PLACEHOLDER_AREA_IMAGE = "/placeholder.svg";

/** Bundled photo for a slug (Santa Teresa ships them), else the placeholder. */
export const catalogImageFor = (slug: string): string => getAreaBySlug(slug)?.image ?? PLACEHOLDER_AREA_IMAGE;

export const splitLines = (s: string): string[] => s.split("\n").map((x) => x.trim()).filter(Boolean);
export const splitCSV = (s: string): string[] => s.split(",").map((x) => x.trim()).filter(Boolean);

export function rowToArea(row: AreaRow, center: [number, number] | null = null): Area {
  const hasPin = row.lat != null && row.lng != null;
  return {
    id: row.id,
    tenantId: row.tenant_id,
    slug: row.slug,
    name: row.name,
    shortDescription: row.short_description ?? "",
    tags: row.tags ?? [],
    image: catalogImageFor(row.slug),
    coordinates: hasPin ? [row.lng as number, row.lat as number] : center ?? [0, 0],
    location: { bullets: row.location_bullets ?? [] },
    knownFor: row.known_for ?? [],
    bestFor: row.best_for ?? "",
    vibe: row.vibe ?? "",
    access: row.access ?? "",
    seoTitle: row.seo_title ?? undefined,
    seoDescription: row.seo_description ?? undefined,
    seoText: row.seo_text ?? "",
    published: row.published,
    hasPin,
    fromCatalog: false,
    thingsToDo: parseThingsToDo(row.things_to_do),
  };
}

export function catalogToArea(a: AreaData, tenantId: string): Area {
  return { ...a, id: null, tenantId, published: true, hasPin: true, fromCatalog: true, thingsToDo: [] };
}

/** The code catalog filtered to a city's list, in the city's order. */
export function catalogAreasForTenant(tenant: { id: string; areaSlugs: string[] }): Area[] {
  return tenant.areaSlugs
    .map((slug) => getAreaBySlug(slug))
    .filter((a): a is AreaData => !!a)
    .map((a) => catalogToArea(a, tenant.id));
}

/** Sort by the city's Locations order; unknown slugs go last, by name. */
export function orderByTenantList<T extends { slug: string; name: string }>(list: T[], areaSlugs: string[]): T[] {
  const idx = new Map(areaSlugs.map((s, i) => [s, i]));
  return [...list].sort((a, b) => {
    const ia = idx.get(a.slug) ?? Number.MAX_SAFE_INTEGER;
    const ib = idx.get(b.slug) ?? Number.MAX_SAFE_INTEGER;
    return ia !== ib ? ia - ib : a.name.localeCompare(b.name);
  });
}

export function areaContentOf(a: AreaData): AreaContent {
  return {
    shortDescription: a.shortDescription ?? "",
    tags: a.tags ?? [],
    bestFor: a.bestFor ?? "",
    vibe: a.vibe ?? "",
    access: a.access ?? "",
    locationBullets: a.location?.bullets ?? [],
    knownFor: a.knownFor ?? [],
    seoTitle: a.seoTitle ?? "",
    seoDescription: a.seoDescription ?? "",
    seoText: a.seoText ?? "",
    thingsToDo: "thingsToDo" in a && Array.isArray((a as { thingsToDo?: unknown }).thingsToDo) ? ((a as { thingsToDo: ThingToDo[] }).thingsToDo) : [],
  };
}

/** The catalog's content for a slug, if the catalog knows it. */
export const catalogContentFor = (slug: string): AreaContent | null => {
  const a = getAreaBySlug(slug);
  return a ? areaContentOf(a) : null;
};

export const emptyAreaContent = (): AreaContent => ({
  shortDescription: "",
  tags: [],
  bestFor: "",
  vibe: "",
  access: "",
  locationBullets: [],
  knownFor: [],
  seoTitle: "",
  seoDescription: "",
  seoText: "",
  thingsToDo: [],
});

const nul = (s: string) => (s.trim() ? s.trim() : null);

/** Snake-cased columns for an upsert (empty strings become NULL). */
export function contentToRow(c: AreaContent) {
  return {
    short_description: nul(c.shortDescription),
    tags: c.tags,
    best_for: nul(c.bestFor),
    vibe: nul(c.vibe),
    access: nul(c.access),
    location_bullets: c.locationBullets,
    known_for: c.knownFor,
    seo_title: nul(c.seoTitle),
    seo_description: nul(c.seoDescription),
    seo_text: nul(c.seoText),
    things_to_do: thingsToDoToJson(c.thingsToDo),
  };
}

/** The editable prose as it sits in form fields (lists as text). */
export interface AreaContentDraft {
  shortDescription: string;
  tags: string;
  bestFor: string;
  vibe: string;
  access: string;
  locationBullets: string;
  knownFor: string;
  seoTitle: string;
  seoDescription: string;
  seoText: string;
  thingsToDo: ThingToDo[];
}

export const draftFromContent = (c: AreaContent): AreaContentDraft => ({
  shortDescription: c.shortDescription,
  tags: c.tags.join(", "),
  bestFor: c.bestFor,
  vibe: c.vibe,
  access: c.access,
  locationBullets: c.locationBullets.join("\n"),
  knownFor: c.knownFor.join("\n"),
  seoTitle: c.seoTitle,
  seoDescription: c.seoDescription,
  seoText: c.seoText,
  thingsToDo: c.thingsToDo,
});

export const contentFromDraft = (d: AreaContentDraft): AreaContent => ({
  shortDescription: d.shortDescription.trim(),
  tags: splitCSV(d.tags),
  bestFor: d.bestFor.trim(),
  vibe: d.vibe.trim(),
  access: d.access.trim(),
  locationBullets: splitLines(d.locationBullets),
  knownFor: splitLines(d.knownFor),
  seoTitle: d.seoTitle.trim(),
  seoDescription: d.seoDescription.trim(),
  seoText: d.seoText.trim(),
  thingsToDo: d.thingsToDo.filter((t) => t.name.trim()),
});

/** Display name for a slug: the city's Locations list first, then the catalog. */
export function areaNameFor(slug: string, tenant?: { areas: string[]; areaSlugs: string[] } | null): string {
  const i = tenant ? tenant.areaSlugs.indexOf(slug) : -1;
  if (i >= 0 && tenant?.areas[i]) return tenant.areas[i];
  return getAreaBySlug(slug)?.name ?? slug;
}
